import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Input, Typography } from '@canvas-logic/clp-shared-ui';

import TooltipContainer from '@/ui-kit/components/Tooltip/TooltipContainer';
import { Popover, usePopoverRef } from '@/ui-kit/components/Popover';
import { useGetShareLink } from '@/ui-kit/hooks';

import style from './ShareButton.module.css';
import { copyToClipboard } from '@/helpers/utils';

interface ShareButtonProps {}

const ShareButton: React.FC<ShareButtonProps> = () => {
  const { t: tTranslation } = useTranslation('translation');
  const { t: tShareLink } = useTranslation('translation', { keyPrefix: 'configuratorPage.shareLink' });
  const { state, requestLink, link, reset } = useGetShareLink();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showCopyMessage, setShowCopyMessage] = useState(false);
  const popoverRef = usePopoverRef();

  const handleClick = () => {
    if (popoverOpen) {
      popoverRef.current?.close();
    } else {
      popoverRef.current?.open();
      void requestLink();
    }
  };

  const handleCopyLink = async () => {
    if (!link) {
      return;
    }

    setShowCopyMessage(true);
    setTimeout(() => {
      setShowCopyMessage(false);
    }, 3000);
    await copyToClipboard(link);
  };

  return (
    <Popover
      ref={popoverRef}
      trigger="none"
      closeOnClickOutside
      placement="bottom-start"
      padding={{
        x: -48,
      }}
      content={
        <TooltipContainer className={style.tooltipContainer} size="large">
          <div className={style.tooltipContent}>
            <div className={style.text}>
              <Typography variant="heading6">{tShareLink('title')}</Typography>
              <Typography variant="paragraph-md" color="description">
                {tShareLink('description')}
              </Typography>
            </div>
            <div className={style.controls}>
              <Input
                label=""
                placeholder={state === 'loading' ? 'Generating link...' : undefined}
                defaultValue={link}
                readOnly
              />
              {showCopyMessage ? (
                <Button className={style.button} variant="ghost" disabled>
                  {tTranslation('buttons.linkCopied')}
                </Button>
              ) : (
                <Button data-copy-link-button className={style.button} variant="primary" onClick={handleCopyLink}>
                  {tShareLink('copyLink')}
                </Button>
              )}
            </div>
          </div>
        </TooltipContainer>
      }
      animate
      onOpen={() => setPopoverOpen(true)}
      onClose={() => {
        setPopoverOpen(false);
        reset();
      }}
      useProvidedRoot
    >
      <Button
        variant="ternary"
        onClick={handleClick}
        disabled={state === 'loading'}
        iconStart={popoverOpen ? 'close' : 'share'}
      />
    </Popover>
  );
};

export default ShareButton;
